import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { getAllNotifications, getAllOrders, getAllCustomers, markNotificationAsRead } from '../../services/api';
import './Notification.css';

var fmtINR = function (n) { return '\u20B9' + (Number(n) || 0).toLocaleString('en-IN'); };

var timeAgo = function (dateStr) {
  if (!dateStr) return '';
  var diff  = Date.now() - new Date(dateStr).getTime();
  var mins  = Math.floor(diff / 60000);
  var hours = Math.floor(diff / 3600000);
  var days  = Math.floor(diff / 86400000);
  if (mins < 1)   return 'Just now';
  if (mins < 60)  return mins + 'm ago';
  if (hours < 24) return hours + 'h ago';
  if (days < 7)   return days + 'd ago';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

var fmtDateTime = function (dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true
  });
};

var getStatusColor = function (status) {
  var s = (status || '').toLowerCase();
  if (s === 'delivered')     return { bg: 'rgba(16,185,129,0.15)',  color: '#34D399' };
  if (s === 'cancelled')     return { bg: 'rgba(239,68,68,0.15)',   color: '#F87171' };
  if (s === 'shipped')       return { bg: 'rgba(59,130,246,0.15)',  color: '#60A5FA' };
  if (s === 'processing')    return { bg: 'rgba(245,158,11,0.15)',  color: '#FCD34D' };
  if (s === 'out of stock')  return { bg: 'rgba(239,68,68,0.18)',   color: '#EF4444' };
  if (s === 'low stock')     return { bg: 'rgba(245,158,11,0.18)',  color: '#F59E0B' };
  return { bg: 'rgba(236,72,153,0.15)', color: '#F472B6' };
};

var buildNotifs = function (notifications) {
  return (Array.isArray(notifications) ? notifications : [])
    .filter(function (n) { return n && n._id; })
    .map(function (n) {
      var status    = n.status || 'Pending';
      var createdAt = n.createdAt || new Date().toISOString();
      var isStockAlert = status === 'Low Stock' || status === 'Out of Stock';
      var productId = resolveObjectId(n.productId);
      return {
        id:      n._id,
        title:   n.title   || 'Notification',
        message: n.message || '',
        amount:  fmtINR(n.amount || 0),
        status:  status,
        time:    createdAt,
        ts:      new Date(createdAt).getTime(),
        shortId: '#' + (n._id || '').slice(-8).toUpperCase(),
        isRead:  n.isRead || false,
        navPath: isStockAlert && productId
          ? '/admin/products?productId=' + encodeURIComponent(productId)
          : undefined,
      };
    })
    .sort(function (a, b) { return b.ts - a.ts; });
};

var resolveObjectId = function (value) {
  if (!value) return '';
  if (typeof value === 'object') return value._id || value.id || '';
  return String(value);
};

var resolveOrderCustomerName = function (order) {
  if (!order) return '';
  if (order.user && typeof order.user === 'object') return order.user.name || order.user.fullName || order.user.email || '';
  if (order.userId && typeof order.userId === 'object') return order.userId.name || order.userId.fullName || order.userId.email || '';
  if (order.customer && typeof order.customer === 'object') return order.customer.name || order.customer.fullName || order.customer.email || '';
  if (order.customerId && typeof order.customerId === 'object') return order.customerId.name || order.customerId.fullName || order.customerId.email || '';
  return order.customerName || order.customerEmail || order.email || '';
};

var resolveOrderCustomerEmail = function (order) {
  if (!order) return '';
  if (order.user && typeof order.user === 'object') return order.user.email || '';
  if (order.userId && typeof order.userId === 'object') return order.userId.email || '';
  if (order.customer && typeof order.customer === 'object') return order.customer.email || '';
  if (order.customerId && typeof order.customerId === 'object') return order.customerId.email || '';
  return order.customerEmail || order.email || '';
};

var READ_ORDER_NOTIFS_KEY = 'adminReadOrderNotifications';

var readOrderNotificationIds = function () {
  try {
    var raw = localStorage.getItem(READ_ORDER_NOTIFS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

var writeOrderNotificationIds = function (ids) {
  try {
    var list = Array.isArray(ids) ? ids : [ids];
    var unique = Array.from(new Set(list.filter(function (id) { return id; })));
    localStorage.setItem(READ_ORDER_NOTIFS_KEY, JSON.stringify(unique));
    return unique;
  } catch (e) {
    return [];
  }
};

var markOrderNotificationsRead = function (ids) {
  var existing = readOrderNotificationIds();
  var list = Array.isArray(ids) ? ids : [ids];
  return writeOrderNotificationIds(existing.concat(list));
};

var buildOrderNotifs = function (orders, customers) {
  var customerMap = {};
  (Array.isArray(customers) ? customers : []).forEach(function (c) {
    if (c && c._id) customerMap[String(c._id)] = c.name || c.fullName || c.email || 'Guest';
  });
  var readIds = readOrderNotificationIds();
  return (Array.isArray(orders) ? orders : [])
    .filter(function (o) { return o && o._id; })
    .map(function (order) {
      var createdAt = order.createdAt || order.updatedAt || new Date().toISOString();
      var status = order.orderStatus || order.status || 'Pending';
      var amount = order.totalAmount || order.total || 0;
      var customerId = resolveObjectId(order.customerId || order.customer || order.userId || order.user);
      var customerName = customerMap[customerId] || resolveOrderCustomerName(order) || 'Guest';
      var customerEmail = resolveOrderCustomerEmail(order);
      var customerLabel = customerName + (customerEmail ? ' (' + customerEmail + ')' : '');
      return {
        id: 'order-' + order._id,
        orderId: order._id,
        title: 'New order received',
        message: customerLabel + ' placed an order',
        amount: amount,
        status: status,
        time: createdAt,
        ts: new Date(createdAt).getTime(),
        shortId: '#' + order._id.slice(-8).toUpperCase(),
        isRead: readIds.indexOf(order._id) !== -1,
        type: 'order',
        navPath: '/admin/orders?orderId=' + encodeURIComponent(order._id),
      };
    })
    .sort(function (a, b) { return b.ts - a.ts; })
    .slice(0, 25);
};

var mergeNotifications = function (notifications, orderNotifications) {
  var merged = [];
  var seen = new Set();

  var pushItem = function (item) {
    if (!item) return;
    var key = item.orderId ? 'order:' + item.orderId : 'notif:' + (item.id || item._id);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  };

  (Array.isArray(notifications) ? notifications : []).forEach(pushItem);
  (Array.isArray(orderNotifications) ? orderNotifications : []).forEach(pushItem);

  return merged.sort(function (a, b) {
    var aTs = a && a.ts ? a.ts : 0;
    var bTs = b && b.ts ? b.ts : 0;
    return bTs - aTs;
  });
};

var NotificationsPage = function () {
  var _nn = useState([]);    var notifs     = _nn[0]; var setNotifs     = _nn[1];
  var _ul = useState(true);  var loading    = _ul[0]; var setLoading    = _ul[1];
  var _rf = useState(false); var refreshing = _rf[0]; var setRefreshing = _rf[1];
  var _ld = useState(false); var pageLoaded = _ld[0]; var setPageLoaded = _ld[1];

  var fetchNotifications = useCallback(function (isManual) {
    return Promise.allSettled([getAllNotifications(), getAllOrders(), getAllCustomers()])
      .then(function (res) {
        var notificationRes = res[0];
        var ordersRes = res[1];
        var customersRes = res[2];
        var notificationRaw = (notificationRes && notificationRes.status === 'fulfilled' && notificationRes.value && notificationRes.value.data && (Array.isArray(notificationRes.value.data) ? notificationRes.value.data : notificationRes.value.data.data || notificationRes.value.data.notifications || [])) || [];
        var orderList = [];
        if (ordersRes && ordersRes.status === 'fulfilled' && ordersRes.value && ordersRes.value.data) {
          var ordersData = ordersRes.value.data;
          if (Array.isArray(ordersData)) orderList = ordersData;
          else if (Array.isArray(ordersData.data)) orderList = ordersData.data;
          else if (Array.isArray(ordersData.orders)) orderList = ordersData.orders;
          else if (Array.isArray(ordersData.result)) orderList = ordersData.result;
        }
        var customerList = customersRes && customersRes.status === 'fulfilled' ? (customersRes.value && customersRes.value.data ? (Array.isArray(customersRes.value.data) ? customersRes.value.data : customersRes.value.data.data || []) : []) : [];
        setNotifs(mergeNotifications(buildNotifs(notificationRaw), buildOrderNotifs(orderList, customerList)));
      })
      .catch(function () { setNotifs([]); })
      .finally(function () {
        setLoading(false);
        if (isManual) setTimeout(function () { setRefreshing(false); }, 600);
      });
  }, []);

  useEffect(function () {
    setLoading(true);
    fetchNotifications(false);
    var timer = setInterval(function () { fetchNotifications(false); }, 15000);
    setPageLoaded(true);
    return function () { clearInterval(timer); };
  }, [fetchNotifications]);

  useEffect(function () {
    var handler = function () { fetchNotifications(false); };
    window.addEventListener('adminNotifUpdated', handler);
    return function () { window.removeEventListener('adminNotifUpdated', handler); };
  }, [fetchNotifications]);

  var handleRefresh = function () {
    setRefreshing(true);
    fetchNotifications(true);
  };

  var unreadCount = notifs.filter(function (n) { return !n.isRead; }).length;

  var navigate = useNavigate();
  var handleNotificationClick = function (notif) {
    if (!notif) return;
    setNotifs(function (prev) {
      return (Array.isArray(prev) ? prev : []).map(function (n) {
        if (!n) return n;
        if (n.id !== notif.id) return n;
        return Object.assign({}, n, { isRead: true });
      });
    });
    if (notif.orderId) {
      markOrderNotificationsRead([notif.orderId]);
    } else if (notif.id) {
      markNotificationAsRead(notif.id).catch(function () {});
    }
    if (notif.navPath) {
      navigate(notif.navPath);
      return;
    }
    if (notif.orderId) {
      navigate('/admin/orders?orderId=' + encodeURIComponent(notif.orderId));
    }
  };

  return React.createElement(AdminLayout, null,

    React.createElement('div', { className: 'np-page' + (pageLoaded ? ' is-loaded' : '') },

      /* ── Header ── */
      React.createElement('div', { className: 'np-hdr' },
        React.createElement('div', { className: 'np-hdr-left' },
          React.createElement('button', {
            className: 'np-back-btn',
            onClick: function () { return navigate(-1); },
            style: { marginRight: 12, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(196,181,253,0.12)', background: 'transparent', color: '#C4B5FD', cursor: 'pointer', fontWeight: 700 }
          }, '\u2190 Back'),
          React.createElement('div', { className: 'page-header-title-row' },
            React.createElement('div', { className: 'page-header-icon' },
              React.createElement('svg', { width: '20', height: '20', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2.5', strokeLinecap: 'round', strokeLinejoin: 'round' },
                React.createElement('path', { d: 'M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9' }),
                React.createElement('path', { d: 'M13.73 21a2 2 0 0 1-3.46 0' })
              )
            ),
            React.createElement('div', null,
              React.createElement('h1', { className: 'page-title' }, 'Notifications'),
              React.createElement('div', { className: 'header-subtitle' },
                unreadCount > 0
                  ? unreadCount + ' unread notification' + (unreadCount !== 1 ? 's' : '')
                  : 'All caught up · Auto-refreshes every 15s'
              )
            )
          )
        ),
        React.createElement('button', {
          className: 'np-btn-refresh' + (refreshing ? ' spinning' : ''),
          onClick: handleRefresh
        },
          React.createElement('svg', {
            width: '14', height: '14', viewBox: '0 0 24 24',
            fill: 'none', stroke: 'currentColor',
            strokeWidth: '2.5', strokeLinecap: 'round', strokeLinejoin: 'round'
          },
            React.createElement('polyline', { points: '23 4 23 10 17 10' }),
            React.createElement('path', { d: 'M20.49 15a9 9 0 1 1-2.12-9.36L23 10' })
          ),
          refreshing ? 'Refreshing\u2026' : 'Refresh'
        )
      ),

      /* ── Panel ── */
      React.createElement('div', { className: 'np-panel' },

        /* Panel header — count only, no buttons */
        React.createElement('div', { className: 'np-panel-hdr' },
          React.createElement('span', { className: 'np-all-label' },
            'All Notifications (' + notifs.length + ')'
          )
        ),

        /* Body */
        loading
          ? React.createElement('div', { className: 'np-loading' },
              React.createElement('div', { className: 'np-spinner' }),
              React.createElement('span', { className: 'np-loading-txt' }, 'Loading notifications\u2026')
            )
          : notifs.length === 0
            ? React.createElement('div', { className: 'np-empty' },
                React.createElement('div', { className: 'np-empty-icon' }, '\uD83D\uDCED'),
                React.createElement('p', { className: 'np-empty-title' }, 'No notifications yet'),
                React.createElement('p', { className: 'np-empty-sub' }, 'New order notifications will appear here.')
              )
            : React.createElement('div', { className: 'np-list' },
                notifs.map(function (n) {
                  var isNew     = !n.isRead;
                  var statusCol = getStatusColor(n.status);
                  return React.createElement('div', {
                    key: n.id,
                    className: 'np-item' + (isNew ? ' is-new' : ' is-read'),
                    onClick: function () { return handleNotificationClick(n); },
                    style: { cursor: n.navPath || n.orderId ? 'pointer' : 'default' }
                  },
                    /* NEW badge for unread, READ badge for read */
                    isNew
                      ? React.createElement('span', { className: 'np-new-badge' }, 'NEW')
                      : React.createElement('span', { className: 'np-read-badge' }, 'Read'),

                    React.createElement('div', { className: 'np-body' },
                      React.createElement('p', { className: 'np-item-title' }, n.title),
                      n.message && React.createElement('p', { className: 'np-item-message' }, n.message),
                      React.createElement('div', { className: 'np-item-row' },
                        React.createElement('span', { className: 'np-amount' }, n.amount),
                        React.createElement('span', { className: 'np-order-id' }, n.shortId),
                        React.createElement('span', {
                          className: 'np-status-pill',
                          style: { background: statusCol.bg, color: statusCol.color }
                        }, n.status)
                      ),
                      React.createElement('div', { className: 'np-item-footer' },
                        React.createElement('span', { className: 'np-time' }, timeAgo(n.time)),
                        React.createElement('span', { className: 'np-footer-dot' }, '\u00B7'),
                        React.createElement('span', null, fmtDateTime(n.time))
                      )
                    )
                  );
                })
              ),

        /* Live bar */
        !loading && React.createElement('div', { className: 'np-live-bar' },
          React.createElement('div', { className: 'np-live-dot' }),
          'Live \u00B7 Auto-refreshes every 15 seconds'
        )
      )
    )
  );
};

export default NotificationsPage;

